import type React from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import heroBg from '../assets/hero-bg.jpg';
import finalConcept from '../assets/Final-Concept-12.png';
import { handleAnchorClick } from '../utils/smoothScroll';
import { mobileQuery, reducedMotionQuery } from '../utils/mediaQueries';
import { useRAFScroll } from '../hooks/useRAFScroll';
import './Hero.css';

const RIPPLE_DURATION_MS = 450;
const BADGE_MAX_SHIFT_PX = 60;
const BADGE_SCROLL_FACTOR = 0.06;

const CONTENT_SCROLL_FACTOR = mobileQuery.matches ? 0.15 : 0.2;

function scrollToNext(): void {
  window.scrollBy({ top: window.innerHeight, behavior: 'smooth' });
}

export default function Hero(): React.JSX.Element {
  const [rippling, setRippling] = useState(false);
  const rippleTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const badgeRef = useRef<HTMLImageElement>(null);
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          el.classList.add('hero--zooming');
          observer.disconnect();
        }
      },
      { threshold: 0.1 },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  useRAFScroll(() => {
    if (reducedMotionQuery.matches) {
      if (contentRef.current) contentRef.current.style.transform = '';
      if (badgeRef.current) badgeRef.current.style.transform = '';
      return;
    }
    const y = window.scrollY;
    if (contentRef.current) {
      contentRef.current.style.transform = `translateY(${y * CONTENT_SCROLL_FACTOR}px)`;
    }
    if (badgeRef.current) {
      const shift = mobileQuery.matches ? 0 : Math.min(y * BADGE_SCROLL_FACTOR, BADGE_MAX_SHIFT_PX);
      badgeRef.current.style.transform = `translateY(${shift}px)`;
    }
  });

  const handleLearnMore = useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    setRippling(true);
    if (rippleTimer.current) clearTimeout(rippleTimer.current);
    rippleTimer.current = setTimeout(() => {
      setRippling(false);
      scrollToNext();
    }, RIPPLE_DURATION_MS);
  }, []);

  useEffect(() => {
    return () => {
      if (rippleTimer.current) clearTimeout(rippleTimer.current);
    };
  }, []);

  return (
    <section
      id="hero-section"
      className="hero"
      style={{ '--hero-bg': `url(${heroBg})` } as React.CSSProperties}
      ref={sectionRef}
      aria-labelledby="hero-heading"
    >
      <div className="hero__overlay" aria-hidden="true" />
      <div className="hero__content" ref={contentRef}>
        <h1 id="hero-heading" className="hero__heading">
          Platform-led Integrated Energy Infrastructure, Delivered End-to-End.
        </h1>
        <p className="hero__subheading">
          Grid-scale battery storage, renewable generation, and co-located AI-grade data
          centre capacity — developed across high-value rural precincts in the NEM.
        </p>
        <div className="hero__cta">
          <button
            className={`hero__btn hero__btn--primary${rippling ? ' hero__btn--ripple' : ''}`}
            onClick={handleLearnMore}
          >
            Learn More
          </button>
          <a
            href="#for-landowners"
            className="hero__btn hero__btn--secondary"
            onClick={handleAnchorClick}
          >
            Partner With Us
          </a>
        </div>
      </div>
      <img
        src={finalConcept}
        alt="Future Energy Responsibly Grounded"
        className="hero__badge"
        ref={badgeRef}
      />
      <button className="hero__scroll-arrow" aria-label="Scroll to next section" onClick={scrollToNext}>
        <svg
          xmlns="http://www.w3.org/2000/svg"
          strokeLinecap="round"
          strokeLinejoin="round"
          clipRule="evenodd"
          viewBox="0 0 25 14"
          aria-hidden="true"
          focusable="false"
        >
          <path fill="none" stroke="#ffffff" strokeWidth="2" d="M1 1l11 11L23 1" />
        </svg>
      </button>
    </section>
  );
}
